/**
 * Initial per-distributor-user notification preferences (create-time).
 *
 * Lists the distributor users that will be in the new chat — the distributor's
 * bot user (its primary order recipient) plus any explicitly added distributor
 * users — each with a switch for whether they receive new-order notifications.
 * Turning one off adds its user id to `deselectedOrderPreferences`; the create
 * flow persists that (sanitized to the chat's users) via the V2 notification
 * mutation after `chatCreate`. Buyer users are never in scope.
 */

import type { CreateChatFormData } from '@choco-admin/types';
import { Card, CardBody, Switch } from '@heroui/react';
import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiBell } from 'react-icons/fi';

interface DistributorRecipient {
  id: string;
  label: string;
  sublabel?: string;
}

export const NotificationPreferencesSection: React.FC = () => {
  const { watch, setValue } = useFormContext<CreateChatFormData>();

  const supplier = watch('supplier');
  const supplierUsers = watch('supplierUsers');
  const deselected = watch('deselectedOrderPreferences');

  const recipients = useMemo<DistributorRecipient[]>(() => {
    const list: DistributorRecipient[] = [];
    const botUserId = supplier?.metaData?.botUserId;
    if (botUserId) {
      list.push({
        id: botUserId,
        label: supplier?.metaData?.name || supplier?.label || 'Distributor',
        sublabel: 'Distributor (default recipient)',
      });
    }
    (supplierUsers ?? []).forEach((user) => {
      list.push({
        id: user.value,
        label: user.label,
        sublabel: user.metaData?.email ?? undefined,
      });
    });
    return list;
  }, [supplier, supplierUsers]);

  const handleToggle = useCallback(
    (userId: string, isEnabled: boolean) => {
      const current = deselected ?? [];
      let next: string[];
      if (isEnabled) {
        // Re-enable: drop the user from the deselected list.
        next = current.filter((id) => id !== userId);
      } else if (current.includes(userId)) {
        next = current;
      } else {
        // Disable: add the user to the deselected list.
        next = [...current, userId];
      }
      setValue('deselectedOrderPreferences', next, { shouldValidate: true });
    },
    [deselected, setValue]
  );

  if (recipients.length === 0) {
    return null;
  }

  return (
    <Card data-testid="NotificationPreferencesSection">
      <CardBody className="space-y-4">
        <div className="flex items-center gap-2">
          <FiBell className="text-primary" />
          <h4 className="font-medium text-foreground">
            Distributor Notifications
          </h4>
        </div>
        <p className="text-sm text-default-600">
          Choose which distributor users receive new-order notifications for
          this chat. Everyone is notified by default.
        </p>

        <div className="rounded-lg border border-default-200 divide-y divide-default-200">
          {recipients.map((recipient) => {
            const isEnabled = !(deselected ?? []).includes(recipient.id);
            return (
              <div
                key={recipient.id}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {recipient.label}
                  </p>
                  {recipient.sublabel && (
                    <p className="text-xs text-default-500 truncate">
                      {recipient.sublabel}
                    </p>
                  )}
                </div>
                <Switch
                  size="sm"
                  isSelected={isEnabled}
                  onValueChange={(value) => handleToggle(recipient.id, value)}
                  aria-label={`Notifications for ${recipient.label}`}
                />
              </div>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
};
